import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Lock, PhoneCall, ClipboardList, Compass } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { LegalDisclosure } from "@/components/site/Footer";
import { PHONE, PHONE_TEL } from "@/lib/fda";

export const Cta = ({ id = "cta", className = "" }) => (
  <Link to="/check-my-options" data-testid={id}
    className={`group inline-flex items-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-extrabold uppercase tracking-[0.12em] text-white transition-transform hover:-translate-y-0.5 ${className}`}>
    Check My Options
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
  </Link>
);

export const FAQS = [
  ["Does it cost anything to get in touch?", "No. Freedom Debt Advice does not charge you a fee for the information we provide and there is no obligation. If we refer you to one of our partners, we may receive a referral fee from them."],
  ["What happens after I complete the assessment?", "Your details are sent securely to our team. We will review the information you have provided and may contact you to talk through your situation before deciding whether it is appropriate to introduce you to a relevant partner."],
  ["Will completing the form affect my credit file?", "No. Answering the questions on this website is not a credit application and we do not carry out a credit search when you complete the assessment."],
  ["Are you the company that will give me debt advice?", "No. RJA Solutions Ltd is not authorised and regulated by the Financial Conduct Authority. We connect you to one of our qualified professionals who are FCA regulated to get you the best advice."],
  ["How long does the assessment take?", "Most people complete it in a few minutes. It is one short question per screen and you can go back at any point."],
  ["Can I ask you to stop contacting me?", "Yes. Tell us at any time and we will record your request and stop contacting you. You can also email info@freedomdebtadvice.co.uk."],
];

const STEPS = [
  [ClipboardList, "Answer a few questions", "Tell us about your debts, your income and your circumstances. No credit check, no obligation."],
  [PhoneCall, "Speak to our team", "We will review what you have told us and may get in touch to understand your situation properly."],
  [Compass, "Understand your options", "Where appropriate, we introduce you to a relevant FCA-regulated partner who can advise you."],
];

const DEBT_TYPES = ["Credit cards", "Personal loans", "Overdrafts", "Store cards", "Short-term loans", "Other unsecured debts"];

export default function Home() {
  return (
    <div data-testid="home-page">
      <section className="grain relative overflow-hidden bg-forest px-5 pb-24 pt-16 text-sand lg:px-10 lg:pb-32 lg:pt-24">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-moss/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 left-1/4 h-96 w-96 rounded-full bg-terracotta/20 blur-3xl" />
        <img src="/logo-mark.png" alt="" aria-hidden="true"
          className="pointer-events-none absolute -right-10 top-10 hidden w-[420px] opacity-[0.07] lg:block" />
        <div className="relative mx-auto max-w-7xl">
          <p className="rise mb-6 inline-flex items-center gap-2 rounded-full border border-sand/20 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-sand/70">
            Free initial assessment
          </p>
          <h1 className="rise max-w-3xl font-display text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            Struggling with debt?
          </h1>
          <p className="rise mt-6 max-w-2xl text-lg font-semibold text-sand/90 md:text-lg" style={{ animationDelay: "160ms" }}>
            Find out what options may be available for your situation.
          </p>
          <p className="rise mt-4 max-w-xl text-base text-sand/70" style={{ animationDelay: "220ms" }}>
            Answer a few simple questions about your debts and circumstances. It only takes a few minutes.
          </p>
          <div className="rise mt-10 flex flex-wrap items-center gap-5" style={{ animationDelay: "300ms" }}>
            <Cta id="hero-cta" />
            <a href={`tel:${PHONE_TEL}`} data-testid="hero-phone"
              className="inline-flex items-center gap-2 text-sm font-bold text-sand hover:text-white">
              <PhoneCall className="h-4 w-4" /> {PHONE}
            </a>
          </div>
          <ul className="rise mt-14 grid gap-4 sm:grid-cols-3" style={{ animationDelay: "380ms" }}>
            {[["Free initial assessment", CheckCircle2], ["No obligation", CheckCircle2], ["Secure and confidential", Lock]].map(([t, Icon]) => (
              <li key={t} className="flex items-center gap-3 rounded-xl border border-sand/15 bg-sand/5 px-4 py-3 text-sm font-semibold">
                <Icon className="h-4 w-4 text-moss" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-5 py-20 lg:px-10" data-testid="reassurance-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-bold text-forest sm:text-4xl">Debt has a way of making everything else louder.</h2>
            <p className="mt-6 text-base leading-relaxed text-forest-soft">
              Missed payments, letters you would rather not open, calls you would rather not take. Whatever stage
              you are at, having a clear picture of your situation is the first useful step.
            </p>
            <p className="mt-4 text-base leading-relaxed text-forest-soft">
              Our short assessment helps our team understand your circumstances so that any conversation you have
              with us is a useful one. We will never tell you that a particular solution is guaranteed, and nothing
              here is a decision or an approval.
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-white p-8">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta">What this is not</p>
            <ul className="mt-5 space-y-4 text-sm text-forest-soft">
              {["Not a credit application", "Not a debt write-off promise", "Not an approval for any solution", "Not regulated advice from us"].map((t) => (
                <li key={t} className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-sand-deep px-5 py-20 lg:px-10" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-display text-3xl font-bold text-forest sm:text-4xl">How it works</h2>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {STEPS.map(([Icon, title, body], i) => (
              <div key={title} className="rounded-2xl border border-border bg-white p-8 transition-transform hover:-translate-y-1">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-lg bg-forest text-sand"><Icon className="h-5 w-5" /></span>
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-forest-soft">Step {i + 1}</span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-forest">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-forest-soft">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 py-20 lg:px-10" data-testid="debt-types-section">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-display text-3xl font-bold text-forest sm:text-4xl">Types of debt people ask us about</h2>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DEBT_TYPES.map((d) => (
              <div key={d} className="flex items-center gap-3 rounded-xl border border-border bg-white px-5 py-4 text-sm font-semibold text-forest">
                <CheckCircle2 className="h-4 w-4 text-moss" /> {d}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-20 lg:px-10" data-testid="why-us-section">
        <div className="mx-auto max-w-7xl rounded-3xl bg-forest px-8 py-14 text-sand lg:px-14">
          <h2 className="font-display text-3xl font-bold sm:text-4xl">Why contact Freedom Debt Advice</h2>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {[["No cost to you", "We do not charge you for the information we provide, and there are no upfront or arrangement fees."],
              ["A real conversation", "You speak to our team, not an automated decision engine."],
              ["Regulated partners", "Where appropriate we introduce you to FCA-regulated professionals."],
              ["Your data, handled properly", "We are ICO registered and only collect what we need."]].map(([t, b]) => (
              <div key={t}>
                <h3 className="font-display text-lg font-bold">{t}</h3>
                <p className="mt-2 text-sm text-sand/70">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-5 pb-20 lg:px-10" data-testid="faq-section">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-3xl font-bold text-forest sm:text-4xl">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map(([q, a], i) => (
              <AccordionItem key={q} value={`i${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-forest">{q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-forest-soft">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-sand-deep px-5 py-20 text-center lg:px-10" data-testid="final-cta-section">
        <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-forest sm:text-4xl">Find out what options may be available for your situation.</h2>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Cta id="final-cta" />
        </div>
      </section>

      <LegalDisclosure />
    </div>
  );
}
